import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Frame } from "@bitesite/react-figstrap";
import { Button, Link, LoadingMessage, inEmbedMode } from "../../odc-ui";

import { tokenizeSquareCard } from "../../utilities/square_helpers";
import BooleanSelection from "./common/boolean_selection";
import TreatmentCategorySelection from "./common/treatment_category_selection";
import SubscriptionPlanSelection from "./auto_grow/subscription_plan_selection";
import SubscriptionPlanVariationSelection from "./auto_grow/subscription_plan_variation_selection";
import ProductSelection from "./common/product_selection";
import SquareCardSelection from "./common/square_card_selection";
import SquareCardForm from "./common/square_card_form";
import UserFields from "./common/user_fields";
import ConfirmationModal from "./auto_grow/confirmation_modal";
import ErrorModal from "./common/error_modal";
import ExternalPurchaseSuccess from "./common/external_purchase_success";

/* 2 modes: 
   - Parent controls treatmentCategory: Pass in initialTreatmentCategory and hideTreatmentCategorySelector = true
   - This component controls treatmentCategory: Don't pass in initialTreatmentCategory and hideTreatmentCategorySelector = false
*/
const AddFundsAutoGrow = ({
  initialTreatmentCategory,
  hideTreatmentCategorySelector,
  renderPreamble,
  external,
  theme,
}) => {
  const authenticated = window.gon.current_user;

  const [treatmentCategory, setTreatmentCategory] = useState(
    initialTreatmentCategory
  );

  const [
    checkingIfAuthenticatedUserHasSquareCards,
    setCheckingIfAuthenticatedUserHasSquareCards,
  ] = useState(true);
  const [authenticatedUserHasSquareCards, setAuthenticatedUserHasSquareCards] =
    useState(false);

  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState();
  const [
    selectedSubscriptionPlanVariation,
    setSelectedSubscriptionPlanVariation,
  ] = useState();
  const [selectedSquareCard, setSelectedSquareCard] = useState();
  const [selectedProduct, setSelectedProduct] = useState();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const [showExternalPurchaseSuccess, setShowExternalPurchaseSuccess] =
    useState(false);

  const [submittingForm, setSubmittingForm] = useState(false);

  /* For unauthenticated flow */
  const [hasExistingAccount, setHasExistingAccount] = useState();

  const [newSquareCard, setNewSquareCard] = useState();
  const [cardBrand, setCardBrand] = useState();
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [phone, setPhone] = useState();
  const [selectedDateOfBirthMonthOption, setSelectedDateOfBirthMonthOption] =
    useState({});
  const [selectedDateOfBirthDayOption, setSelectedDateOfBirthDayOption] =
    useState({});

  const checkIfAuthenticatedUserHasSquareCards = () => {
    if (authenticated) {
      setCheckingIfAuthenticatedUserHasSquareCards(true);
      $.getJSON("/square_cards", (results) => {
        if (results.length > 0) {
          setAuthenticatedUserHasSquareCards(true);
        } else {
          setAuthenticatedUserHasSquareCards(false);
        }
        setCheckingIfAuthenticatedUserHasSquareCards(false);
      });
    }
  };

  const validateForm = () => {
    if (!selectedSubscriptionPlan) {
      window.alert("Please specify what plan you'd like to subscribe to.");
      return false;
    }

    if (!selectedSubscriptionPlanVariation) {
      window.alert(
        "Please specify how frequently you'd like to make payments."
      );
      return false;
    }

    if (!selectedProduct) {
      window.alert(
        "Please specify how much you'd like to contribute each cycle."
      );
      return false;
    }

    if (
      authenticated &&
      authenticatedUserHasSquareCards &&
      !selectedSquareCard
    ) {
      window.alert("Please specify credit card you'd like to use");
      return false;
    }

    if (
      !authenticated &&
      ((!hasExistingAccount &&
        (!firstName ||
          !lastName ||
          !email ||
          !phone ||
          !selectedDateOfBirthMonthOption ||
          !selectedDateOfBirthDayOption)) ||
        (hasExistingAccount && !email))
    ) {
      window.alert("Please fill out all of your information");
      return false;
    }

    return true;
  };

  const checkFormAndShowConfirmationModal = () => {
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  const createSubscriptionForUnauthenticatedUser = async () => {
    if (!submittingForm) {
      setSubmittingForm(true);
      let tokenizationSucceeded = false;
      let token;

      try {
        token = await tokenizeSquareCard(newSquareCard);
        tokenizationSucceeded = true;
      } catch (error) {
        setSubmittingForm(false);
        setShowError(true);
        setShowConfirmation(false);
        setErrorMessage("There was an error with your credit card details.");
      }

      if (tokenizationSucceeded) {
        $.ajax({
          url: "/subscriptions/create_for_unauthenticated_user",
          method: "POST",
          data: {
            square_card: {
              token,
            },
            user: {
              email,
              profile_attributes: {
                first_name: firstName,
                last_name: lastName,
                phone,
                date_of_birth_month: selectedDateOfBirthMonthOption.value,
                date_of_birth_day: selectedDateOfBirthDayOption.value,
              },
            },
            subscription: {
              square_item_id: selectedProduct.square_item_id,
              square_item_variation_id:
                selectedProduct.square_item_variation_id,
              square_subscription_plan_id:
                selectedSubscriptionPlan.square_subscription_plan_id,
              square_subscription_plan_variation_id:
                selectedSubscriptionPlanVariation.square_subscription_plan_variation_id,
            },
            treatment_category: treatmentCategory,
            has_existing_account: hasExistingAccount,
          },
          success: () => {
            setShowExternalPurchaseSuccess(true);
            setSubmittingForm(false);
          },
          error: (jqXHR) => {
            if (jqXHR.responseJSON && jqXHR.responseJSON.errors.length > 0) {
              setErrorMessage(jqXHR.responseJSON.errors[0]);
            }
            setSubmittingForm(false);
            setShowError(true);
            setShowConfirmation(false);
          },
        });
      }
    }
  };

  const createSubscriptionForAuthenticated = async () => {
    if (!submittingForm) {
      setSubmittingForm(true);
      let tokenizationSucceeded = false;
      let token;

      const subscription = {
        square_item_id: selectedProduct.square_item_id,
        square_item_variation_id: selectedProduct.square_item_variation_id,
        square_subscription_plan_id:
          selectedSubscriptionPlan.square_subscription_plan_id,
        square_subscription_plan_variation_id:
          selectedSubscriptionPlanVariation.square_subscription_plan_variation_id,
      };

      const data = {
        subscription,
        treatment_category: treatmentCategory,
      };

      if (authenticatedUserHasSquareCards) {
        data.subscription.square_card_id = selectedSquareCard.id;
      } else {
        try {
          token = await tokenizeSquareCard(newSquareCard);
          tokenizationSucceeded = true;
        } catch (error) {
          setSubmittingForm(false);
          setShowError(true);
          setShowConfirmation(false);
          setErrorMessage("There was an error with your credit card details.");
        }

        data.square_card = { token };
      }

      if (authenticatedUserHasSquareCards || tokenizationSucceeded) {
        $.ajax({
          url: "/subscriptions",
          method: "POST",
          data,
          success: () => {
            setSubmittingForm(false);
            if (external) {
              setShowExternalPurchaseSuccess(true);
            } else {
              window.location.href = "/subscriptions?show_congrats=true";
            }
          },
          error: (jqXHR) => {
            if (jqXHR.responseJSON && jqXHR.responseJSON.errors.length > 0) {
              setErrorMessage(jqXHR.responseJSON.errors[0]);
            }
            setSubmittingForm(false);
            setShowError(true);
            setShowConfirmation(false);
          },
        });
      }
    }
  };

  const submitForm = () => {
    if (authenticated) {
      createSubscriptionForAuthenticated();
    } else {
      createSubscriptionForUnauthenticatedUser();
    }
  };

  const showSubscribeButton = () => {
    if (authenticated) {
      return (
        selectedSquareCard ||
        (!authenticatedUserHasSquareCards && selectedProduct)
      );
    } else {
      return selectedProduct && hasExistingAccount !== undefined;
    }
  };

  useEffect(() => {
    if (hideTreatmentCategorySelector) {
      setTreatmentCategory(initialTreatmentCategory);
    }
  }, [initialTreatmentCategory]);

  useEffect(() => {
    if (authenticated) {
      checkIfAuthenticatedUserHasSquareCards();
    } else {
      setCheckingIfAuthenticatedUserHasSquareCards(false);
    }
  }, []);

  if (showExternalPurchaseSuccess) {
    return (
      <ExternalPurchaseSuccess
        hasExistingAccount={hasExistingAccount}
        message="Your order has been successfully placed and your Auto Grow subscription
        is now active."
        detailsUrl="/subscriptions"
        newAccountMessage="You have also been registered for a Healthcoin® Rewards account to manage your subscription. You will receive an email with further instructions."
      />
    );
  }

  return (
    <div>
      {renderPreamble && renderPreamble()}
      {checkingIfAuthenticatedUserHasSquareCards ? (
        <LoadingMessage message="Please wait..." />
      ) : (
        <Frame vertical gap={32}>
          {!hideTreatmentCategorySelector && (
            <TreatmentCategorySelection
              caption="Which type of treatment would you like to use Auto Grow for?"
              treatmentCategory={treatmentCategory}
              onTreatmentCategoryClick={setTreatmentCategory}
              theme={theme}
            />
          )}

          {treatmentCategory && (
            <SubscriptionPlanSelection
              selectedSubscriptionPlan={selectedSubscriptionPlan}
              onSubscriptionPlanClick={setSelectedSubscriptionPlan}
              theme={theme}
              showOnlyIfThereIsMoreThanOnePlan
            />
          )}

          {selectedSubscriptionPlan && (
            <SubscriptionPlanVariationSelection
              subscriptionPlan={selectedSubscriptionPlan}
              theme={theme}
              selectedSubscriptionPlanVariation={
                selectedSubscriptionPlanVariation
              }
              onSubscriptionPlanVariationClick={
                setSelectedSubscriptionPlanVariation
              }
            />
          )}

          {selectedSubscriptionPlanVariation && (
            <ProductSelection
              purchaseMode="auto_grow"
              treatmentCategory={treatmentCategory}
              subscriptionPlan={selectedSubscriptionPlan}
              theme={theme}
              selectedProduct={selectedProduct}
              onProductClick={setSelectedProduct}
            />
          )}

          {selectedProduct &&
            (authenticated && authenticatedUserHasSquareCards ? (
              <SquareCardSelection
                onSelectedSquareCardChange={setSelectedSquareCard}
                theme={theme}
              />
            ) : (
              <>
                <SquareCardForm
                  onSquareCardFieldsChange={(newSquareCard, newCardBrand) => {
                    setNewSquareCard(newSquareCard);
                    setCardBrand(newCardBrand);
                  }}
                />
              </>
            ))}

          {!authenticated && selectedProduct && (
            <BooleanSelection
              question={
                <>
                  Auto Grow subscriptions are set up through{" "}
                  <Link
                    type={inEmbedMode() ? "embed" : ""}
                    href="/"
                    target="_blank"
                  >
                    Healthcoin®
                  </Link>
                  . Do you already have a Healthcoin® account?
                </>
              }
              selectedValue={hasExistingAccount}
              onOptionClick={setHasExistingAccount}
              theme={theme}
            />
          )}

          {hasExistingAccount !== undefined && !authenticated && (
            <UserFields
              prompt={
                hasExistingAccount ? (
                  <>
                    Please enter the email address associated with your{" "}
                    <Link
                      type={inEmbedMode() ? "embed" : ""}
                      href="/"
                      target="_blank"
                    >
                      Healthcoin®
                    </Link>{" "}
                    account
                  </>
                ) : (
                  "Please enter your information"
                )
              }
              enabledFields={
                hasExistingAccount
                  ? ["email"]
                  : ["firstName", "lastName", "email", "phone", "dateOfBirth"]
              }
              firstName={firstName}
              lastName={lastName}
              email={email}
              phone={phone}
              onFirstNameChange={setFirstName}
              onLastNameChange={setLastName}
              onEmailChange={setEmail}
              onPhoneChange={setPhone}
              selectedDateOfBirthMonthOption={selectedDateOfBirthMonthOption}
              onSelectedDateOfBirthMonthOptionChange={
                setSelectedDateOfBirthMonthOption
              }
              selectedDateOfBirthDayOption={selectedDateOfBirthDayOption}
              onSelectedDateOfBirthDayOptionChange={
                setSelectedDateOfBirthDayOption
              }
            />
          )}

          {showSubscribeButton() && (
            <Button
              type={
                inEmbedMode()
                  ? "embed"
                  : theme === "paige"
                  ? "dark-paige"
                  : "dark"
              }
              onClick={(e) => {
                e.preventDefault();
                checkFormAndShowConfirmationModal();
              }}
              textTransform="none"
            >
              Subscribe
            </Button>
          )}
        </Frame>
      )}

      {showConfirmation && (
        <ConfirmationModal
          authenticated={authenticated}
          hasExistingAccount={hasExistingAccount}
          selectedSubscriptionPlan={selectedSubscriptionPlan}
          selectedSubscriptionPlanVariation={selectedSubscriptionPlanVariation}
          selectedProduct={selectedProduct}
          cardBrand={cardBrand}
          selectedSquareCard={selectedSquareCard}
          firstName={firstName}
          lastName={lastName}
          email={email}
          phone={phone}
          selectedDateOfBirthMonthOption={selectedDateOfBirthMonthOption}
          selectedDateOfBirthDayOption={selectedDateOfBirthDayOption}
          onSubmitClick={submitForm}
          submittingForm={submittingForm}
          theme={theme}
          onCloseClick={() => setShowConfirmation(false)}
        />
      )}
      {showError && (
        <ErrorModal
          caption="We're sorry, but there was an error creating your Auto Grow subscription. Please check the errors below, and try again."
          errorMessage={errorMessage}
          onTryAgainClick={() => {
            setShowError(false);
          }}
          onCloseClick={() => setShowError(false)}
        />
      )}
    </div>
  );
};

AddFundsAutoGrow.propTypes = {
  initialTreatmentCategory: PropTypes.string,
  hideTreatmentCategorySelector: PropTypes.bool,
  external: PropTypes.bool,
  renderPreamble: PropTypes.func,
  theme: PropTypes.string,
};

export default AddFundsAutoGrow;
